'use client';

import Image from 'next/image';
import { getDocsUrl } from '@/lib/docsLinks';
import { useLandingLocale, useLandingTranslations } from './LandingLocaleProvider';
import { Reveal } from './Reveal';

const hosts = [
  { name: 'Orca', icon: '/agents/orca.svg', docsPath: 'setup/orca', labelKey: 'hostIntegrationDocsOrca' },
  { name: 'herdr', icon: '/agents/herdr.png', docsPath: 'setup/herdr', labelKey: 'hostIntegrationDocsHerdr' },
] as const;

const steps = [
  {
    titleKey: 'hostIntegrationStep1Title',
    descKey: 'hostIntegrationStep1Desc',
    tone: 'bg-secondary/10 text-secondary',
  },
  {
    titleKey: 'hostIntegrationStep2Title',
    descKey: 'hostIntegrationStep2Desc',
    tone: 'bg-primary/10 text-primary',
  },
  {
    titleKey: 'hostIntegrationStep3Title',
    descKey: 'hostIntegrationStep3Desc',
    tone: 'bg-info/10 text-info',
  },
  {
    titleKey: 'hostIntegrationStep4Title',
    descKey: 'hostIntegrationStep4Desc',
    tone: 'bg-success/10 text-[var(--chip-success-fg)]',
  },
] as const;

export function WorkspaceHostSection() {
  const t = useLandingTranslations();
  const { locale } = useLandingLocale();

  return (
    <Reveal>
      <section className="relative mt-8 overflow-hidden rounded-2xl border border-primary/20 bg-base-100 p-6 shadow-xs md:p-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.10),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.08),transparent_30%)]"
        />

        <div className="relative">
          <div className="max-w-3xl">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              {t('hostIntegrationEyebrow')}
            </p>
            <h3 className="text-xl font-bold tracking-tight md:text-2xl">{t('hostIntegrationTitle')}</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-base-content/65">
              {t('hostIntegrationSubtitle')}
            </p>
          </div>

          <div className="mt-7 grid gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] md:items-stretch">
            {steps.map((step, index) => (
              <div key={step.titleKey} className="contents">
                <div className="rounded-xl border border-base-300 bg-base-100/85 p-4 shadow-xs">
                  <span
                    className={`mb-3 flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${step.tone}`}
                  >
                    {index + 1}
                  </span>
                  <p className="text-sm font-semibold">{t(step.titleKey)}</p>
                  <p className="mt-1 text-xs leading-relaxed text-base-content/55">{t(step.descKey)}</p>
                </div>
                {index < steps.length - 1 ? (
                  <div className="flex items-center justify-center py-1 text-primary/40 md:px-1 md:py-0">
                    <span className="rotate-90 text-lg md:rotate-0">→</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-2 rounded-xl bg-base-200/45 px-4 py-3 text-xs sm:flex-row sm:items-center sm:justify-between">
            <span className="font-semibold text-base-content/70">{t('hostIntegrationOutcomeTitle')}</span>
            <span className="text-base-content/50">{t('hostIntegrationOutcomeDescription')}</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs">
            {hosts.map((host) => (
              <a
                key={host.name}
                href={getDocsUrl(host.docsPath, locale)}
                target="_blank"
                rel="noopener noreferrer"
                className="link link-primary link-hover inline-flex items-center gap-1.5 font-medium"
              >
                <Image
                  src={host.icon}
                  alt=""
                  aria-hidden="true"
                  width={16}
                  height={16}
                  className="h-4 w-4 object-contain dark:invert"
                />
                {t(host.labelKey)} →
              </a>
            ))}
          </div>
        </div>
      </section>
    </Reveal>
  );
}
